<script setup lang="ts">
import { ref, computed } from 'vue';
import { useWishlistStore } from '~/store/wishlist';
import { useCartStore } from '~/store/cart';

const props = defineProps<{
  itemId: string;
  isArchived?: boolean;
}>();

const wishlistStore = useWishlistStore();
const cartStore = useCartStore();
const { $lang } = useNuxtApp();
const runtimeConfig = useRuntimeConfig();

const isProcessing = ref(false);

const isInWishlist = computed(() => {
  return wishlistStore.isInWishlist(props.itemId);
});

const isLoggedIn = computed(() => {
  const hasCustomer = !!cartStore.cartData?.user?.email;
  return hasCustomer;
});

const handleClick = async (event: Event) => {
  event.preventDefault();
  event.stopPropagation();

  // Don't allow wishlist actions on archived products
  if (props.isArchived) {
    return;
  }

  // Check if user is logged in
  if (!isLoggedIn.value) {
    // Open account menu to prompt login
    cartStore.accountMenuMessage = $lang('wishlist', 'wishlist_login_message');
    cartStore.showAccountMenu = true;
    return;
  }

  if (isProcessing.value) {
    return;
  }

  isProcessing.value = true;

  try {
    if (isInWishlist.value) {
      // Find the wishlist item and use its line ID (wishlistItem ID)
      const displayItem = props.itemId.split('-')[0];
      const wishlistItem = wishlistStore.items.find(item => item.item === displayItem);
      if (wishlistItem) {
        await wishlistStore.removeItem(wishlistItem.line);
      }
    } else {
      // Add to wishlist
      await wishlistStore.addItem(props.itemId);
    }
  } catch (error) {
    console.error('[WishlistToggle] Error:', error);
  } finally {
    isProcessing.value = false;
  }
};
</script>

<template>
  <button
    v-if="!isArchived && runtimeConfig.public.accountPagesEnabled"
    class="wishlist-toggle"
    :class="{ 'is-in-wishlist': isInWishlist, 'is-processing': isProcessing }"
    :title="isInWishlist ? $lang('wishlist', 'remove_from_wishlist') : $lang('wishlist', 'add_to_wishlist')"
    @click="handleClick"
  >
    <fa
      :icon="['fa' + (isInWishlist ? 's' : 'l'), 'heart']"
      :class="{ 'animate-pulse': isProcessing }"
    />
  </button>
</template>

<style scoped lang="postcss">
.wishlist-toggle {
  @apply absolute top-12 left-12 z-10;
  @apply w-44 h-44 flex items-center justify-center;
  @apply transition-all duration-200;
  @apply cursor-pointer;
  
  /* Remove focus outline on mouse click, but keep it for keyboard navigation */
  outline: none;
  
  /* Custom focus ring for keyboard navigation - subtle but visible */
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
    border-radius: 4px;
  }
  
  &:hover {
    @apply scale-110;
  }
  
  &:active {
    @apply scale-95;
  }
  
  svg {
    @apply text-xl transition-all duration-200;
    @apply text-black-50;
  }
  
  &.is-in-wishlist svg {
    @apply text-red-full;
  }
  
  &:hover svg {
    @apply text-red-75;
  }
  
  &.is-processing {
    @apply pointer-events-none opacity-70;
  }
}

/* Mobile - slightly smaller */
@screen mobOnly {
  .wishlist-toggle {
    @apply w-40 h-40 top-8 left-8;
    
    svg {
      @apply text-lg;
    }
  }
}
</style>
